/* =====================================================================
 * Dashboard — shared layout primitives.
 *
 * Every colour, radius and breakpoint comes from the theme so the page
 * inherits the tenant's brand rather than restating it. No magic hexes.
 *
 * Mobile posture: the page is a single scrolling column under
 * `breakpoints.mobile`. Cards keep their padding but lose the grid,
 * and anything intrinsically wide (the year heatmap, a long series)
 * scrolls inside its own card instead of widening the page.
 * ===================================================================== */

import styled, { css, keyframes } from 'styled-components';

export const PageScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

export const PageInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 32px 24px 64px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px 16px 48px;
    gap: 16px;
  }
`;

export const PageHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 4px;
`;

export const PageHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const refreshSpin = keyframes`
  to { transform: rotate(360deg); }
`;

/** Icon-only refresh. Spins only for a user-triggered page refresh. */
export const RefreshButton = styled.button<{ $busy: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
    ${({ $busy }) =>
      $busy &&
      css`
        animation: ${refreshSpin} 0.8s linear infinite;
      `}
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.borderInput};
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      animation: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    /* Comfortable touch target. */
    width: 38px;
    height: 38px;
  }
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/** 페이지 헤더 우측 액션 묶음 (새로고침 / 내보내기). */
export const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

/**
 * 아이콘 + 글자 액션 버튼. ``RefreshButton`` 과 같은 높이/테두리를 쓰되
 * 라벨이 들어가므로 폭이 내용에 맞춰 늘어난다.
 */
export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  height: 34px;
  padding: 0 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  svg {
    width: 15px;
    height: 15px;
  }

  svg.spin {
    animation: ${refreshSpin} 0.8s linear infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    svg.spin {
      animation: none;
    }
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.borderInput};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 2px;
  }
`;

export const Card = styled.section`
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  padding: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 16px;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  }
`;

export const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const CardTitle = styled.h2`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const CardHint = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/** ``CardHead`` 밖에서 독립된 한 줄로 쓰는 힌트. ``CardHint`` 는 inline
 *  ``span`` 이라 그대로 내놓으면 아래 내용과 붙는다. 위쪽 음수 마진은
 *  ``CardHead`` 의 ``margin-bottom: 16px`` 을 상쇄해 제목에 붙여 놓는다. */
export const CardHintLine = styled(CardHint)`
  display: block;
  margin: -8px 0 16px;
`;

/** 집계 기간을 바꿔 새 시리즈를 받는 동안 이전 그래프를 흐리게 남겨 둔다.
 *  비워 버리면 카드 높이가 "불러오는 중" 한 줄로 줄었다가 다시 늘어나 화면이 튄다. */
export const ChartFrame = styled.div<{ $loading: boolean }>`
  position: relative;

  & > :not([data-chart-loading]) {
    opacity: ${({ $loading }) => ($loading ? 0.35 : 1)};
    transition: opacity ${({ theme }) => theme.transitions.fast};
  }
`;

export const ChartLoading = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  pointer-events: none;
`;

/** Horizontal scroll container for intrinsically wide content. */
export const ScrollX = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  /* Keep the page itself from scrolling sideways on mobile. */
  max-width: 100%;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 3px;
  }
`;

/**
 * 요약 타일 행.
 *
 * ``$columns`` 는 **반드시 실제 타일 수와 같아야 한다.** 열 수가 더 많으면
 * 남는 칸이 빈 채로 남아 타일 행만 아래 카드들보다 좁아 보인다 (grid 는
 * 자식이 없는 칸도 폭을 차지한다). 기본값 5 는 개인 대시보드의 타일 수다.
 */
export const StatGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 5 }) => $columns}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/** Segmented control used for the day / week / month switch. */
export const Segmented = styled.div`
  display: inline-flex;
  padding: 2px;
  gap: 2px;
  background: ${({ theme }) => theme.colors.bgInput};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
`;

/** Independent metric and time-granularity controls for the usage chart. */
export const SegmentedGroups = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`;

/** Keep the two selectors and their divider together on narrow screens. */
export const SegmentedControlCluster = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  overflow-x: auto;
`;

/** Visual boundary between metric (runs/tokens) and time grouping. */
export const SegmentedDivider = styled.span`
  width: 1px;
  height: 20px;
  flex: 0 0 1px;
  background: ${({ theme }) => theme.colors.border};
`;

export const SegmentedButton = styled.button<{ $active: boolean }>`
  border: none;
  cursor: pointer;
  padding: 5px 12px;
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.bgPrimary : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.textPrimary : theme.colors.textSecondary};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  /* Comfortable touch target without changing the visual height. */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 7px 14px;
  }
`;

export const DateRangeForm = styled.form`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin: -2px 0 16px;
`;

export const DateField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const DateInput = styled.input`
  height: 32px;
  box-sizing: border-box;
  padding: 0 9px;
  border: 1px solid ${({ theme }) => theme.colors.borderInput};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgInput};
  color: ${({ theme }) => theme.colors.textPrimary};
  font: inherit;
  font-size: 0.8125rem;
  color-scheme: ${({ theme }) => theme.mode};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 1px;
  }
`;

/**
 * ``SelectDropdown`` 을 날짜 입력과 같은 줄에 놓기 위한 칸.
 *
 * ``DateField`` 와 달리 ``label`` 이 아니라 ``div`` 다 — 안에 들어가는 것이
 * 네이티브 폼 컨트롤이 아니라 ``button`` 기반 combobox 라서, label 로 감싸도
 * 접근성 이름이 생기지 않는다 (이름은 dropdown 의 ``aria-label`` 이 만든다).
 *
 * ``SelectDropdown`` 은 폭이 100% 라 flex 안에서 0 까지 줄어든다. 부서명이
 * 잘리지 않을 만큼 최소 폭을 준다.
 */
export const DepartmentField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  min-width: 220px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 100%;
  }
`;

export const DateRangeButton = styled.button<{ $primary?: boolean }>`
  height: 32px;
  padding: 0 12px;
  border: 1px solid ${({ theme, $primary }) =>
    $primary ? theme.colors.brand : theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme, $primary }) =>
    $primary ? theme.colors.brand : 'transparent'};
  color: ${({ theme, $primary }) =>
    $primary ? theme.colors.textInverse : theme.colors.textSecondary};
  font-size: 0.8125rem;
  font-weight: 550;
  cursor: pointer;

  &:hover {
    background: ${({ theme, $primary }) =>
      $primary ? theme.colors.brandHover : theme.colors.bgHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 2px;
  }
`;

export const DateRangeError = styled.span`
  align-self: center;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.error};
`;

export const PaginationFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const PaginationCount = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

export const PaginationNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const PaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 2px;
  }
`;

export const PaginationStatus = styled.span`
  min-width: 64px;
  text-align: center;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;
`;
